import Container from "@app/secured/LearningPage/components/Container";
import {
  AssetComponentMetadata,
  AssetContext,
  Badge,
  ContentIntegrationAuthBody,
  ContentIntegrationAuthData,
  FileUploadHandlers,
  GradeActivityMetadata,
  GradeAssetDto,
  GraderFeedbackDto,
  GraderFeedbackV2,
  ResponseChangeProps,
  RoleType,
  useCallbackRef,
} from "@cca/components";
import { useService } from "@providersV2";
import { MediaLibraryService } from "@servicesV2";
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useState } from "react";

import { Loader } from "../Loader";
import { NestableAssetDto, renderAssetsFromDtos } from "../PlayerV2/utils";

export type ActivityPlayerContentProps = {
  nestedAssetDtos: NestableAssetDto[] | undefined;
  onLearnerResponseChange?: (props: ResponseChangeProps) => void;
  onLearnerSubmit?: (assetId: string) => Promise<GraderFeedbackDto | null>;
  onGradeAsset?: (assetId: string, gradeAsset: GradeAssetDto) => Promise<void>;
  onSaveOverallFeedback?: (
    message: string,
    immediatelySave: boolean,
  ) => Promise<void> | undefined;
  noContentMessage: string;
  fileHandlers: FileUploadHandlers;
  metadata: Omit<AssetComponentMetadata, "portalContainer">;
  fetchContentIntegrationAuth?: (
    body: ContentIntegrationAuthBody,
  ) => Promise<ContentIntegrationAuthData | undefined>;
  gradeMetadata?: GradeActivityMetadata;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
};

export const ActivityPlayerContent = forwardRef<
  HTMLDivElement,
  ActivityPlayerContentProps
>(function ActivityPlayerContent(
  {
    nestedAssetDtos,
    noContentMessage,
    metadata,
    gradeMetadata,
    fileHandlers,
    fetchContentIntegrationAuth,
    onLearnerResponseChange,
    onLearnerSubmit,
    onGradeAsset,
    onSaveOverallFeedback,
    onScroll,
  },
  ref,
) {
  const queryClient = useQueryClient();
  const { initialization: mediaLibraryServiceInit } =
    useService(MediaLibraryService);

  const fetchMediaUrlById = useCallbackRef(
    (mediaId: string) =>
      queryClient.fetchQuery({
        queryKey: ["media-library", "urls", { mediaId }],
        queryFn: async () =>
          mediaLibraryServiceInit.then(({ service }) =>
            service.getUrls([mediaId]).then((urls) => urls[mediaId]),
          ),
      }),
    [queryClient, mediaLibraryServiceInit],
  );

  const fetchMediaItemById = useCallbackRef((mediaId: string) =>
    queryClient.fetchQuery({
      queryKey: ["media-library", "items", { mediaId }],
      queryFn: async () =>
        mediaLibraryServiceInit.then(({ service }) =>
          service.getMediaItemById(mediaId),
        ),
    }),
  );

  const handleOverallFeedbackChanged = async (
    message: string,
    immediatelySave: boolean = false,
  ) => onSaveOverallFeedback?.(message, immediatelySave);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  const canViewOverallFeedback =
    Boolean(metadata.role) &&
    metadata.role !== RoleType.Learner &&
    metadata.mode === "review";

  return (
    <div
      className="flex w-full flex-1 flex-col items-center overflow-auto"
      onScroll={onScroll}
      ref={(element) => {
        setPortalContainer(element);

        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      }}
      // `contain: paint` is necessary, because `overflow: hidden` does not clip
      // the children with `position: fixed`, e.g. Radix popovers.
      style={{ contain: "paint" }}
    >
      <div className="max-w-content flex w-full flex-1 flex-col gap-8 py-4 md:py-8">
        {nestedAssetDtos == undefined ? (
          <Container>
            <Loader className="m-auto" />
          </Container>
        ) : nestedAssetDtos.length === 0 ? (
          <Badge
            className="m-auto whitespace-normal"
            color="info"
            icon="circle-info"
            variant="soft"
            wrapText
          >
            {noContentMessage}
          </Badge>
        ) : (
          <>
            {canViewOverallFeedback && (
              <GraderFeedbackV2
                buttonClassName="w-full justify-center bg-gray-50 p-4 text-center"
                buttonText="Overall Feedback"
                metadata={metadata}
                onChange={handleOverallFeedbackChanged}
                onDelete={async () => {
                  handleOverallFeedbackChanged?.("", true);
                }}
                requestState={gradeMetadata?.saveStatus}
                savedOn={gradeMetadata?.savedOn}
                title="OVERALL FEEDBACK"
                value={gradeMetadata?.teacherFeedback || ""}
              />
            )}

            <AssetContext
              fetchContentIntegrationAuth={fetchContentIntegrationAuth}
              mediaLibrary={{ fetchMediaUrlById, fetchMediaItemById }}
            >
              {renderAssetsFromDtos(
                nestedAssetDtos,
                { ...metadata, portalContainer },
                onLearnerResponseChange,
                onLearnerSubmit,
                onGradeAsset,
                fileHandlers,
              )}
            </AssetContext>
          </>
        )}
      </div>
    </div>
  );
});
